import CartCountdownTimerSkeleton from 'app/components/Skeletons/Common/CartCountdownTimer';
import CartStepCardSkeleton from 'app/components/Skeletons/Common/CartStepCardSkeleton';
import CartStepsSkeleton from 'app/components/Skeletons/Common/CartStepsSkeleton';
import { selectVoucherData } from 'app/pages/TicketSalePage/slice/selectors';
import axios, { AxiosError } from 'axios';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  setBillingInfo,
  setSelectedEventAndItems,
  startCountdown,
} from 'store/cartSlice';
import { setMerchantTaxName } from 'store/vendorSlice';
import { getEventDetailsLink } from 'utils/events';
import {
  API_REQUEST_STATUS_NOT_SENT,
  API_REQUEST_STATUS_SUCCESS,
} from 'utils/static/constants';
import useVoucherData from './useVoucherData';

export interface ISelectedTicket {
  id: string;
  quantity: number;
  date: string;
  timeStart: string;
  timeEnd: string;
  idOrderItem: string;
}

export interface IUserBilling {
  name: string;
  companyName: string;
  email: string;
  country: string;
  county: string;
  city: string;
  address: string;
  cui: string;
}

interface TicketDataFromQueryParameters {
  id: string;
  quantity: number;
  date: string;
  timeEnd: string;
  timeStart: string;
}

const RedirectToBilling = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event-id');
  const voucherCode = searchParams.get('voucher');
  const tickets = JSON.parse(searchParams.get('tickets') as string);
  const billing = JSON.parse(searchParams.get('billing') as string);

  const voucherData = useSelector(selectVoucherData);

  const showErrorToast = (message: string) => {
    toast.error(message, {
      position: 'top-center',
      autoClose: false,
    });
  };

  const voucherRequestStatus = useVoucherData(
    eventId,
    voucherCode,
    showErrorToast,
  );

  const reserveTickets = async () => {
    try {
      const data: {
        tickets: TicketDataFromQueryParameters[];
        voucher?: string;
      } = {
        tickets,
      };

      if (voucherCode) {
        data.voucher = voucherCode;
      }

      let {
        data: { uuid, event: eventData, tickets: ticketsData },
      } = await axios.post(
        `${process.env.REACT_APP_API_URL}/events/${eventId}/tickets?include=event,event.dateTimeFormat,event.currency,event.customerform,event.hasRegistrationForm,event.fees_and_taxes,event.tickets,event.tickets.ticketprice,event.tickets.ticketaddons,event.merchantTaxName,tickets.ticketTypeId,tickets.type`,
        data,
      );

      // insert data in cart slice
      eventData = {
        ...eventData,
        ...eventData.fees_and_taxes,
      };

      delete eventData.fees_and_taxes;

      dispatch(
        setSelectedEventAndItems({
          uuid,
          voucher: voucherData.status === 'valid' ? voucherData : null,
          event: eventData,
          tickets: tickets.map(ticket => ({
            ...ticket,
            idOrderItem: ticketsData.find(
              el => el.ticketTypeId === ticket.id && el.type === 'ticket',
            ).id,
          })),
        }),
      );

      dispatch(
        startCountdown({
          vendorId: eventData.id_seller,
          eventId: eventData.id,
        }),
      );

      dispatch(setBillingInfo(billing));

      dispatch(setMerchantTaxName(eventData.merchantTaxName));

      // redirect to billing page
      const billingInfoPageLink =
        getEventDetailsLink(eventData.name, eventData.id) +
        '/buy-tickets/billing-information';

      navigate(billingInfoPageLink, {
        state: {
          hideBackButton: true,
        },
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        showErrorToast(error.response?.data.error.message);
        return;
      }

      navigate('/404');
    }
  };

  useEffect(() => {
    if (
      voucherRequestStatus === API_REQUEST_STATUS_NOT_SENT ||
      voucherRequestStatus === API_REQUEST_STATUS_SUCCESS
    ) {
      reserveTickets();
    }
  }, [voucherRequestStatus]);

  return (
    <div className="page-container">
      <CartCountdownTimerSkeleton />
      <div className="section-container">
        <CartStepsSkeleton />
        <CartStepCardSkeleton />
      </div>
    </div>
  );
};

export default RedirectToBilling;
